import {
  Component,
  computed,
  inject,
  signal,
  viewChild,
  ElementRef,
  ChangeDetectionStrategy,
  afterRenderEffect,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Dialog } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CoVisrStore } from './co-visr.store';
import { CoVisrMarkdownPipe } from './co-visr-markdown.pipe';
import { buildSuggestedQuestions, formatPeriodPhrase } from './co-visr-display.utils';
import { injectDateRangeFromQueryParams } from '../../core/utils/query-params-sync.util';

/** Small enough to tuck the panel aside, large enough to keep the header and input usable. */
const MIN_WIDTH_PX = 256;
const MIN_HEIGHT_PX = 200;
const MIN_MARGIN_PX = 32;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/** Compass points, so a direction can be tested one letter at a time: 'ne' is both 'n' and 'e'. */
type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

@Component({
  selector: 'co-visr-chat',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Dialog, ButtonModule, FormsModule, NgTemplateOutlet, TooltipModule, CoVisrMarkdownPipe],
  styles: `
    /* Fixed from the start: PrimeNG only switches the dialog to fixed once it has been dragged,
       and the resize handle needs the panel itself as its containing block from the first open. */
    /* The panel is sized by an explicit height rather than a bottom anchor, which would squash it
       as it is dragged; top: 14rem and that height leave a 3rem gap along the bottom. */
    /* 14rem clears the date picker on the supervisor view, the lowest one in the app, so the
       period stays reachable with the panel open. */
    ::ng-deep .p-dialog.covisr-dialog {
      position: fixed;
      top: 14rem;
      right: 1rem;
      margin: 0;
      max-height: none;
      /* Offset-free layers reach the top corners, which a drop shadow alone leaves flat on the page. */
      box-shadow:
        0 0 0 1px rgb(0 0 0 / 0.06),
        0 0 24px rgb(0 0 0 / 0.1),
        0 16px 48px -8px rgb(0 0 0 / 0.22);
    }

    ::ng-deep .covisr-dialog .covisr-resize {
      position: absolute;
    }

    ::ng-deep .covisr-dialog .covisr-resize-n,
    ::ng-deep .covisr-dialog .covisr-resize-s {
      left: 0;
      right: 0;
      height: 6px;
      cursor: ns-resize;
    }

    ::ng-deep .covisr-dialog .covisr-resize-e,
    ::ng-deep .covisr-dialog .covisr-resize-w {
      top: 0;
      bottom: 0;
      width: 6px;
      cursor: ew-resize;
    }

    /* Corners overlap the edges, so they sit on top to win the shared pixels. */
    ::ng-deep .covisr-dialog .covisr-resize-ne,
    ::ng-deep .covisr-dialog .covisr-resize-nw,
    ::ng-deep .covisr-dialog .covisr-resize-se,
    ::ng-deep .covisr-dialog .covisr-resize-sw {
      width: 14px;
      height: 14px;
      z-index: 1;
    }

    ::ng-deep .covisr-dialog .covisr-resize-n,
    ::ng-deep .covisr-dialog .covisr-resize-ne,
    ::ng-deep .covisr-dialog .covisr-resize-nw {
      top: 0;
    }

    ::ng-deep .covisr-dialog .covisr-resize-s,
    ::ng-deep .covisr-dialog .covisr-resize-se,
    ::ng-deep .covisr-dialog .covisr-resize-sw {
      bottom: 0;
    }

    ::ng-deep .covisr-dialog .covisr-resize-e,
    ::ng-deep .covisr-dialog .covisr-resize-ne,
    ::ng-deep .covisr-dialog .covisr-resize-se {
      right: 0;
    }

    ::ng-deep .covisr-dialog .covisr-resize-w,
    ::ng-deep .covisr-dialog .covisr-resize-nw,
    ::ng-deep .covisr-dialog .covisr-resize-sw {
      left: 0;
    }

    ::ng-deep .covisr-dialog .covisr-resize-ne,
    ::ng-deep .covisr-dialog .covisr-resize-sw {
      cursor: nesw-resize;
    }

    ::ng-deep .covisr-dialog .covisr-resize-nw,
    ::ng-deep .covisr-dialog .covisr-resize-se {
      cursor: nwse-resize;
    }

    @keyframes thinking-bounce {
      0%,
      80%,
      100% {
        transform: scale(0);
        opacity: 0.4;
      }
      40% {
        transform: scale(1);
        opacity: 1;
      }
    }

    .thinking-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: var(--p-primary-500, #386eff);
      animation: thinking-bounce 1.4s infinite ease-in-out both;
    }

    .thinking-dot:nth-child(1) {
      animation-delay: -0.32s;
    }
    .thinking-dot:nth-child(2) {
      animation-delay: -0.16s;
    }
    .thinking-dot:nth-child(3) {
      animation-delay: 0s;
    }

    @keyframes blink-cursor {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0;
      }
    }

    /* Agent messages are rendered from markdown through [innerHTML]. That content never gets
       the component's encapsulation attribute, so these rules have to be ::ng-deep; the
       .covisr-md namespace keeps them from touching anything else. Element defaults (heading
       sizes, list markers) are restored explicitly because Tailwind's preflight strips them. */
    ::ng-deep .covisr-md {
      line-height: 1.55;
    }

    ::ng-deep .covisr-md > :first-child {
      margin-top: 0;
    }

    ::ng-deep .covisr-md > :last-child {
      margin-bottom: 0;
    }

    ::ng-deep .covisr-md p {
      margin: 0 0 0.5rem;
    }

    ::ng-deep .covisr-md h1,
    ::ng-deep .covisr-md h2,
    ::ng-deep .covisr-md h3,
    ::ng-deep .covisr-md h4,
    ::ng-deep .covisr-md h5,
    ::ng-deep .covisr-md h6 {
      margin: 0.875rem 0 0.375rem;
      font-weight: 600;
      line-height: 1.3;
    }

    ::ng-deep .covisr-md h1 {
      font-size: 1.0625rem;
    }

    ::ng-deep .covisr-md h2 {
      font-size: 1rem;
    }

    ::ng-deep .covisr-md h3 {
      font-size: 0.9375rem;
    }

    ::ng-deep .covisr-md h4,
    ::ng-deep .covisr-md h5,
    ::ng-deep .covisr-md h6 {
      font-size: 0.875rem;
    }

    ::ng-deep .covisr-md ul,
    ::ng-deep .covisr-md ol {
      margin: 0 0 0.5rem;
      padding-left: 1.25rem;
    }

    ::ng-deep .covisr-md ul {
      list-style: disc;
    }

    ::ng-deep .covisr-md ol {
      list-style: decimal;
    }

    ::ng-deep .covisr-md li {
      margin: 0.125rem 0;
    }

    ::ng-deep .covisr-md li > ul,
    ::ng-deep .covisr-md li > ol {
      margin: 0.125rem 0 0;
    }

    ::ng-deep .covisr-md li > p {
      margin: 0;
    }

    ::ng-deep .covisr-md strong {
      font-weight: 600;
    }

    ::ng-deep .covisr-md em {
      font-style: italic;
    }

    ::ng-deep .covisr-md a {
      color: var(--p-primary-600, #2556eb);
      text-decoration: underline;
      overflow-wrap: anywhere;
    }

    ::ng-deep .covisr-md code {
      padding: 0.0625rem 0.25rem;
      border-radius: 0.25rem;
      background-color: var(--p-surface-200, #e5e7eb);
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      font-size: 0.8125rem;
      overflow-wrap: anywhere;
    }

    ::ng-deep .covisr-md pre {
      margin: 0 0 0.5rem;
      padding: 0.5rem 0.625rem;
      border-radius: 0.5rem;
      background-color: var(--p-surface-200, #e5e7eb);
      overflow-x: auto;
    }

    ::ng-deep .covisr-md pre code {
      padding: 0;
      background: none;
      font-size: 0.75rem;
    }

    ::ng-deep .covisr-md blockquote {
      margin: 0 0 0.5rem;
      padding-left: 0.625rem;
      border-left: 3px solid var(--p-surface-300, #d1d5db);
      color: var(--p-surface-600, #4b5563);
    }

    ::ng-deep .covisr-md hr {
      margin: 0.75rem 0;
      border: 0;
      border-top: 1px solid var(--p-surface-300, #d1d5db);
    }

    /* display: block lets a wide table scroll inside the bubble instead of stretching it. */
    ::ng-deep .covisr-md table {
      display: block;
      width: fit-content;
      max-width: 100%;
      margin: 0 0 0.5rem;
      border-collapse: collapse;
      font-size: 0.8125rem;
      overflow-x: auto;
    }

    ::ng-deep .covisr-md th,
    ::ng-deep .covisr-md td {
      padding: 0.25rem 0.5rem;
      border: 1px solid var(--p-surface-300, #d1d5db);
      text-align: left;
    }

    ::ng-deep .covisr-md th {
      background-color: var(--p-surface-200, #e5e7eb);
      font-weight: 600;
    }

    ::ng-deep .app-dark .covisr-md code,
    ::ng-deep .app-dark .covisr-md pre,
    ::ng-deep .app-dark .covisr-md th {
      background-color: var(--p-surface-700, #374151);
    }

    ::ng-deep .app-dark .covisr-md blockquote,
    ::ng-deep .app-dark .covisr-md hr,
    ::ng-deep .app-dark .covisr-md th,
    ::ng-deep .app-dark .covisr-md td {
      border-color: var(--p-surface-600, #4b5563);
    }

    ::ng-deep .app-dark .covisr-md blockquote {
      color: var(--p-surface-300, #d1d5db);
    }

    /* The cursor rides the last rendered block rather than sitting in its own element, which
       would drop to a new line now that the message body is block-level. */
    ::ng-deep .covisr-md-streaming > :last-child::after {
      content: '';
      display: inline-block;
      width: 2px;
      height: 1em;
      margin-left: 2px;
      background-color: var(--p-primary-500, #386eff);
      animation: blink-cursor 0.8s infinite;
      vertical-align: text-bottom;
    }
  `,
  template: `
    <!-- Non-modal so the page stays interactive: Co-Visr acts on whatever the user is looking at.
         Opens docked right like a side panel, then drags and resizes freely from there. -->
    <p-dialog
      [visible]="store.isOpen()"
      (visibleChange)="onVisibleChange($event)"
      position="right"
      styleClass="covisr-dialog"
      [style]="{ width: 'min(36rem, calc(100vw - 2rem))', height: 'calc(100vh - 17rem)' }"
      [modal]="false"
      [focusTrap]="false"
      [resizable]="false"
      [contentStyle]="{ display: 'flex', 'flex-direction': 'column', 'min-height': 0 }"
    >
      <ng-template #header>
        <!-- Absolutely positioned against the panel, so they cover all four edges and corners
             despite living in the header. mousedown is swallowed so PrimeNG's header drag
             doesn't move the panel mid-resize. -->
        @for (direction of resizeDirections; track direction) {
          <div
            class="covisr-resize covisr-resize-{{ direction }}"
            (pointerdown)="startResize($event, direction)"
            (mousedown)="$event.stopPropagation()"
          ></div>
        }
        <div class="flex items-center gap-2">
          <span class="font-bold text-lg">Co-Visr</span>
          <p-button
            icon="pi pi-trash"
            [text]="true"
            severity="secondary"
            pTooltip="Clear chat"
            tooltipPosition="left"
            (click)="store.clearChat()"
            [disabled]="store.isStreaming()"
          />
        </div>
      </ng-template>

      <!-- Shared by the welcome screen and the bottom bar, so only one textarea exists at a time. -->
      <ng-template #composer>
        <div
          class="flex items-end gap-2 rounded-3xl border border-surface-300 bg-surface-0 dark:bg-surface-900 py-1.5 pl-4 pr-1.5 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500"
        >
          <textarea
            #inputField
            rows="1"
            class="flex-1 resize-none max-h-52 overflow-y-auto scrollbar-thin bg-transparent py-2.5 text-sm outline-none"
            placeholder="Ask Co-Visr..."
            [(ngModel)]="inputText"
            (input)="autoResize()"
            (keydown.enter)="onEnter($event)"
            [disabled]="store.isStreaming()"
          ></textarea>
          <p-button
            icon="pi pi-send"
            (click)="send()"
            [disabled]="store.isStreaming() || !inputText().trim()"
            [rounded]="true"
          />
        </div>
      </ng-template>

      @if (isEmpty()) {
        <div class="flex flex-col flex-1 min-h-0 justify-center gap-5 overflow-y-auto scrollbar-thin px-2">
          <div class="flex flex-col items-center gap-2 text-center">
            <i class="pi pi-sparkles text-3xl text-primary-500"></i>
            <h2 class="text-xl font-semibold text-surface-900 dark:text-surface-0">How can Co-Visr help today?</h2>
            <p class="text-sm text-surface-600 dark:text-surface-400">
              Ask about your calls, leads or compliance rules.
            </p>
          </div>
          <ng-container [ngTemplateOutlet]="composer" />
          <div class="flex flex-wrap justify-center gap-2">
            @for (suggestion of suggestions(); track suggestion) {
              <button
                type="button"
                class="rounded-full border border-surface-300 dark:border-surface-600 px-3 py-1.5 text-sm text-surface-700 dark:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-800 hover:border-primary-500 transition-colors"
                (click)="ask(suggestion)"
              >
                {{ suggestion }}
              </button>
            }
          </div>
        </div>
      } @else {
        <!-- flex-1/min-h-0 instead of h-full: the dialog's content box has no explicit height,
             so the message list has to earn its height from the flex chain to scroll. -->
        <div class="flex flex-col flex-1 min-h-0">
          <div #messagesContainer class="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3 pb-4 scrollbar-thin">
            @for (msg of store.displayMessages(); track msg.id) {
              @if (msg.role === 'warning') {
                <!-- Warning rendered by the client (see CoVisrWarning), not by the model, so it reaches
                     the user whatever the reply says. -->
                <div class="flex justify-start">
                  <div
                    class="rounded-2xl py-2 px-4 max-w-[85%] text-sm wrap-break-word bg-amber-50 text-amber-900 border border-amber-200 flex items-start gap-2"
                    role="status"
                  >
                    <i class="pi pi-exclamation-triangle mt-0.5 shrink-0"></i>
                    <span>{{ msg.text }}</span>
                  </div>
                </div>
              } @else {
                <div
                  class="flex"
                  [class.justify-end]="msg.role === 'user'"
                  [class.justify-start]="msg.role === 'assistant'"
                >
                  <div
                    class="rounded-2xl py-2 px-4 max-w-[85%] text-sm wrap-break-word"
                    [class.bg-primary-100]="msg.role === 'user'"
                    [class.text-primary-900]="msg.role === 'user'"
                    [class.bg-surface-100]="msg.role === 'assistant'"
                    [class.text-surface-900]="msg.role === 'assistant'"
                  >
                    @if (msg.role === 'assistant') {
                      <!-- Agent replies arrive as markdown. Bound as a plain string so Angular's
                         sanitizer runs over the generated HTML. -->
                      <div
                        class="covisr-md"
                        [class.covisr-md-streaming]="store.isReceivingText() && isLastAssistantMessage(msg.id)"
                        [innerHTML]="msg.text | coVisrMarkdown"
                      ></div>
                    } @else {
                      <span class="whitespace-pre-wrap">{{ msg.text }}</span>
                    }
                  </div>
                </div>
              }
            }

            @if (store.isWaitingForResponse()) {
              <div class="flex justify-start">
                <div class="rounded-2xl py-3 px-5 bg-surface-100 flex items-center gap-1.5">
                  <div class="thinking-dot"></div>
                  <div class="thinking-dot"></div>
                  <div class="thinking-dot"></div>
                </div>
              </div>
            }

            @if (store.error(); as error) {
              <div class="flex justify-start">
                <div class="rounded-2xl py-2 px-4 bg-red-50 text-red-700 text-sm max-w-[85%]">
                  {{ error }}
                </div>
              </div>
            }
          </div>

          <div class="flex flex-col gap-2 pt-3 border-t border-surface-200">
            @if (store.hasPendingConfirmations()) {
              <div class="flex items-center gap-2">
                <span class="text-sm text-surface-700 dark:text-surface-300">{{ confirmationPromptText() }}</span>
                <p-button
                  label="Yes"
                  severity="success"
                  (click)="store.confirmTools(true)"
                  [disabled]="store.isStreaming()"
                />
                <p-button
                  label="No"
                  severity="secondary"
                  [outlined]="true"
                  (click)="store.confirmTools(false)"
                  [disabled]="store.isStreaming()"
                />
              </div>
            } @else {
              <ng-container [ngTemplateOutlet]="composer" />
            }
          </div>
        </div>
      }
    </p-dialog>
  `,
})
export class CoVisrChatComponent {
  readonly store = inject(CoVisrStore);
  readonly resizeDirections: ResizeDirection[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
  /** Follows the page's period picker, so the welcome questions ask about the range on screen. */
  readonly #dateRange = injectDateRangeFromQueryParams();
  readonly suggestions = computed(() => buildSuggestedQuestions(formatPeriodPhrase(this.#dateRange())));
  readonly inputText = signal('');
  readonly messagesContainer = viewChild<ElementRef<HTMLDivElement>>('messagesContainer');
  readonly inputField = viewChild<ElementRef<HTMLTextAreaElement>>('inputField');

  readonly lastAssistantMessageId = computed(() => {
    const msgs = this.store.displayMessages();
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === 'assistant') return msgs[i].id;
    }
    return null;
  });

  readonly isEmpty = computed(
    () =>
      this.store.messages().length === 0 &&
      !this.store.isStreaming() &&
      !this.store.error() &&
      !this.store.hasPendingConfirmations(),
  );

  readonly confirmationPromptText = computed(() => {
    const names = this.store.pendingConfirmationToolNames();
    return names.length > 0 ? `Execute ${names.join(', ')}?` : 'Execute?';
  });

  constructor() {
    afterRenderEffect(() => {
      this.store.displayMessages();
      this.store.isWaitingForResponse();
      const container = this.messagesContainer()?.nativeElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    });

    // Welcome screen only: focusing mid-conversation would steal focus from the page.
    afterRenderEffect(() => {
      if (this.store.isOpen() && this.isEmpty()) {
        this.inputField()?.nativeElement.focus();
      }
    });
  }

  /**
   * Resizes the panel from any edge or corner. PrimeNG's own grip refuses to grow a panel that
   * already touches the viewport edge, which is every docked position, so sizing is handled here.
   */
  startResize(event: PointerEvent, direction: ResizeDirection): void {
    const handle = event.target as HTMLElement;
    const panel = handle.closest('.p-dialog') as HTMLElement | null;
    if (!panel) return;

    event.preventDefault();
    handle.setPointerCapture(event.pointerId);

    const startX = event.clientX;
    const startY = event.clientY;
    const start = panel.getBoundingClientRect();
    // Until the panel has been dragged it is anchored by `right`, while the maths below assumes
    // left/top. Pinning it to its own coordinates and dropping `right` leaves one anchor, rather
    // than leaning on the over-constrained rule that only ignores `right` while the page is LTR.
    panel.style.left = `${start.left}px`;
    panel.style.top = `${start.top}px`;
    panel.style.right = 'auto';

    const onMove = (moveEvent: PointerEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;

      // Dragging an edge moves it and leaves the opposite one where it is.
      if (direction.includes('e')) {
        panel.style.width = `${clamp(start.width + dx, MIN_WIDTH_PX, window.innerWidth - start.left - MIN_MARGIN_PX)}px`;
      }
      if (direction.includes('w')) {
        const width = clamp(start.width - dx, MIN_WIDTH_PX, start.right - MIN_MARGIN_PX);
        panel.style.width = `${width}px`;
        panel.style.left = `${start.right - width}px`;
      }
      if (direction.includes('s')) {
        panel.style.height = `${clamp(start.height + dy, MIN_HEIGHT_PX, window.innerHeight - start.top - MIN_MARGIN_PX)}px`;
      }
      if (direction.includes('n')) {
        const height = clamp(start.height - dy, MIN_HEIGHT_PX, start.bottom - MIN_MARGIN_PX);
        panel.style.height = `${height}px`;
        panel.style.top = `${start.bottom - height}px`;
      }
    };

    const onEnd = () => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onEnd);
      handle.removeEventListener('pointercancel', onEnd);
    };

    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onEnd);
    handle.addEventListener('pointercancel', onEnd);
  }

  isLastAssistantMessage(id: string): boolean {
    return this.lastAssistantMessageId() === id;
  }

  autoResize(): void {
    const el = this.inputField()?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }

  onEnter(event: Event): void {
    if ((event as KeyboardEvent).shiftKey) return;
    event.preventDefault();
    this.send();
  }

  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.store.closeDrawer();
    }
  }

  ask(question: string): void {
    if (this.store.isStreaming()) return;
    this.store.sendMessage(question);
  }

  send(): void {
    const text = this.inputText().trim();
    if (!text || this.store.isStreaming()) return;
    this.inputText.set('');
    const el = this.inputField()?.nativeElement;
    if (el) el.style.height = 'auto';
    this.store.sendMessage(text);
  }
}
